import React, { useEffect, useMemo, useState } from 'react'
import { Button, Heading, TextField, TextInput } from '../../../../views/components'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import { INVOICE_PREFIX } from '../../../api/accounting/constants'
import styles from './UpdateInvoiceNumberPanel.module.scss'

/** Digits only. The value is parsed and range-checked before it can be sent. */
const DIGITS_ONLY = /^[0-9]{1,7}$/

// Counter moves by about the number of agencies billed, so bigger jumps are
// usually a typo. Warns rather than blocks — a real correction can be large.
const PLAUSIBLE_RAISE = 500
const PLAUSIBLE_DROP = 100

type TProps = {
  lastUsed: number | null
  saving?: boolean
  onCancel: () => void
  onSave: (value: number) => void
}

// Field takes the last used number because that is what the API takes, but
// the preview shows the resulting invoice number so nobody does the +1.
const UpdateInvoiceNumberPanel = ({ lastUsed, saving = false, onCancel, onSave }: TProps) => {
  const [raw, setRaw] = useState('')
  const [touched, setTouched] = useState(false)

  // Seed from the current counter each time the panel is opened for a new
  // value, so the starting point is what is actually stored.
  useEffect(() => {
    setRaw(lastUsed === null ? '' : String(lastUsed))
    setTouched(false)
  }, [lastUsed])

  const parsed = useMemo(() => (DIGITS_ONLY.test(raw) ? Number(raw) : null), [raw])

  const error = useMemo(() => {
    if (!touched || raw === '') return null
    if (!DIGITS_ONLY.test(raw)) return 'Digits only, up to 7 of them'
    if (parsed !== null && parsed < 1) return 'Must be 1 or more'
    return null
  }, [touched, raw, parsed])

  const isUnchanged = parsed !== null && parsed === lastUsed
  const canSave = parsed !== null && parsed >= 1 && !isUnchanged && !saving

  const isRewind = parsed !== null && lastUsed !== null && parsed < lastUsed
  const drift = parsed !== null && lastUsed !== null ? parsed - lastUsed : 0
  const isImplausible = drift > PLAUSIBLE_RAISE || -drift > PLAUSIBLE_DROP

  return (
    <form
      className={styles.root}
      onSubmit={event => {
        event.preventDefault()
        if (canSave && parsed !== null) onSave(parsed)
      }}
    >
      <header className={styles.header}>
        <Heading as="h2" className={styles.title}>
          Set invoice number
        </Heading>
        <p className={styles.intro}>
          Enter the last invoice number that has been used. The next billing run starts one above it.
        </p>
      </header>

      <div className={styles.body}>
        <TextField
          label="Last used invoice number"
          error={error ?? undefined}
          inputComponent={
            <TextInput
              type={TextFieldTypes.text}
              name="lastBillingInvoiceNumber"
              inputMode="numeric"
              autoComplete="off"
              placeholder="5317"
              value={raw}
              disabled={saving}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => setRaw(event.target.value.trim())}
              onBlur={() => setTouched(true)}
            />
          }
        />

        {/* Holds a dash while the input is invalid rather than guessing. */}
        <div className={styles.preview}>
          <span className={styles['preview-label']}>Next invoice</span>
          <span className={styles['preview-value']}>
            {parsed !== null && parsed >= 1 ? (
              <>
                <span className={styles['preview-prefix']}>{INVOICE_PREFIX}</span>
                <span>{parsed + 1}</span>
              </>
            ) : (
              <span className={styles['preview-placeholder']}>—</span>
            )}
          </span>
        </div>

        {isRewind && (
          <p className={styles.warn} role="alert">
            <strong>This rewinds the counter.</strong> {INVOICE_PREFIX}
            {(parsed as number) + 1}–{INVOICE_PREFIX}
            {lastUsed} were already counted as used and would be issued again. Only do this if those numbers were never
            actually invoiced.
          </p>
        )}

        {isImplausible && !isRewind && (
          <p className={styles.warn} role="alert">
            <strong>That is a jump of {drift}.</strong> The counter usually moves by about the number of agencies
            billed. Check for an extra digit before saving.
          </p>
        )}

        {isImplausible && isRewind && (
          <p className={styles.warn} role="alert">
            <strong>That is a drop of {-drift}.</strong> Check for a missing digit before saving.
          </p>
        )}

        {isUnchanged && <p className={styles.hint}>Already set to {lastUsed}. Change the number to save.</p>}
      </div>

      <footer className={styles.footer}>
        <Button type="button" secondary onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" disabled={!canSave} loading={saving}>
          {isRewind ? 'Rewind counter' : 'Save'}
        </Button>
      </footer>
    </form>
  )
}

export default UpdateInvoiceNumberPanel
